import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

const DEFAULT_TIME_ZONE = 'Asia/Singapore';
const DEFAULT_DELIVERY_DAYS = 6;

/**
 * Calculates an estimated delivery date by adding `days` calendar days
 * (including Saturdays and Sundays) to "today" as observed in `timeZone`,
 * and formats it as "20 Oct 2026".
 * @param {number} days
 * @param {string} timeZone
 * @returns {string}
 */
function calculateEstimatedDeliveryDate(days, timeZone) {
  const now = new Date();

  const getPart = (parts, type) => Number(parts.find((part) => part.type === type)?.value);

  const todayParts = new Intl.DateTimeFormat('en-SG', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);

  // Build the date from timeZone-local Y/M/D as a UTC date so that adding
  // days isn't affected by the browser's local timezone or DST shifts.
  const deliveryDate = new Date(
    Date.UTC(getPart(todayParts, 'year'), getPart(todayParts, 'month') - 1, getPart(todayParts, 'day'))
  );
  deliveryDate.setUTCDate(deliveryDate.getUTCDate() + days);

  const deliveryParts = new Intl.DateTimeFormat('en-SG', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).formatToParts(deliveryDate);

  const day = deliveryParts.find((part) => part.type === 'day')?.value ?? '';
  const month = deliveryParts.find((part) => part.type === 'month')?.value ?? '';
  const year = deliveryParts.find((part) => part.type === 'year')?.value ?? '';

  return `${day} ${month} ${year}`;
}

class EstimatedDelivery extends HTMLElement {
  connectedCallback() {
    this.update();

    this.closestSection = this.closest('.shopify-section, dialog');
    this.closestSection?.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
  }

  disconnectedCallback() {
    this.closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
  }

  /**
   * @param {CustomEvent} event
   */
  handleVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newElement = event.detail.data.html?.querySelector('estimated-delivery');

    if (!newElement || !(newElement instanceof HTMLElement)) return;

    morph(this, newElement, { childrenOnly: true });
    this.dataset.available = newElement.dataset.available;
    this.dataset.preorder = newElement.dataset.preorder;

    this.update();
  };

  update() {
    const isAvailable = this.dataset.available === 'true';
    const isPreorder = this.dataset.preorder === 'true';
    const shouldShow = isAvailable && !isPreorder;

    this.hidden = !shouldShow;

    if (!shouldShow) return;

    const days = Number(this.dataset.deliveryDays) || DEFAULT_DELIVERY_DAYS;
    const timeZone = this.dataset.timezone || DEFAULT_TIME_ZONE;
    const dateElement = this.querySelector('[data-estimated-delivery-date]');

    if (dateElement) {
      dateElement.textContent = calculateEstimatedDeliveryDate(days, timeZone);
    }
  }
}

if (!customElements.get('estimated-delivery')) {
  customElements.define('estimated-delivery', EstimatedDelivery);
}
